import { type CommonAction, response } from "@iflyrpa/share";
import { decompress } from "fzstd";
import { z } from "zod";
import { AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { DouyinGetMusicParamsSchema } from "./schema";

const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");

export type DouyinGetMusicParams = z.infer<typeof DouyinGetMusicParamsSchema>;
export { DouyinGetMusicParamsSchema } from "./schema";

export interface Music {
    id: string;
    title: string;
    author: string;
    duration: number;
    url: string;
    cover_url: string;
}

export interface DouyinMusicResponse {
    status_code: number;
    status_msg: string;
    music?: Music[];
    total?: number;
    data?: {
        music_list?: Music[];
        total?: number;
    };
}
export interface DouyinMusicAuthResponse {
    status_code: number;
    status_msg: string;
    signature: string;
}
export type DouyinGetMusicAction = CommonAction<
    DouyinGetMusicParams,
    {
        music: Music[];
        total: number;
    }
>;

export const douyinGetMusic: DouyinGetMusicAction = async (_task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    const headers: any = {
        cookie: params.cookies.map((it: any) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.douyin.com",
        referer: "https://creator.douyin.com/",
        Accept: "application/json, text/plain, */*",
        "Accept-Encoding": "gzip, deflate, br, zstd",
        "Content-Type": "application/json",
        pragma: "no-cache",
        "cache-control": "no-cache",
        "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
        "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
        "sec-ch-ua-mobile": "?0",
        "user-agent": userAgent,
        "sec-fetch-site": "same-origin",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
        "accept-language": "zh-CN,zh;q=0.9",
        priority: "u=1, i",
    };
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];
    const http = new Http(...args);

    const csrfToken = generateCsrfTokenAdvanced({
        cookies: params.cookies,
        url: "https://creator.douyin.com/web/api/media/aweme/search/post/auth/",
        method: "GET",
        userAgent,
    });

    const keyword = params.keyword || "";
    const musicParams: any = {
        keyword: keyword || "11",
        search_source: "normal_search",
        count: params.count?.toString() || "20",
        read_aid: "2906",
        aid: "1128",
        cursor: "0",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo?.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo?.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo?.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo?.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo?.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        msToken: params.cookies.find((e: any) => e.name === "msToken")?.value || "",
        a_bogus: "",
    };
    const musicQueryBefore = new URLSearchParams(musicParams).toString();
    const aBogus = getABogus(
        `https://tsearch.amemv.com/openapi/aweme/v1/music/search/?${musicQueryBefore}`,
        {},
        userAgent,
    );
    musicParams.a_bogus = aBogus;
    const queryString = new URLSearchParams(musicParams).toString();
    console.log("抖音获取音乐参数:", queryString);
    //获取权限
    const authorizationParams = {
        read_aid: "2906",
        aid: "1128",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo?.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo?.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo?.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo?.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo?.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        a_bogus: "",
    };
    const authQueryBefore = new URLSearchParams(authorizationParams).toString();
    authorizationParams.a_bogus = getABogus(
        `https://creator.douyin.com/web/api/media/aweme/search/post/auth/?${authQueryBefore}`,
        {},
        userAgent,
    );
    const authorizationQueryString = new URLSearchParams(authorizationParams).toString();
    const authorization = await http.api<DouyinMusicAuthResponse>(
        {
            method: "get",
            url: `https://creator.douyin.com/web/api/media/aweme/search/post/auth/?${authorizationQueryString}`,
            headers: {
                ...headers,
                "X-secsdk-csrf-token": csrfToken,
            },
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );
    if (authorization.status_code !== 0) {
        return response(414, authorization.status_msg, { music: [], total: 0 });
    }
    console.log("抖音获取音乐权限:", authorization);

    const musicRes = await http.api<ArrayBuffer>(
        {
            method: "get",
            url: `https://tsearch.amemv.com/openapi/aweme/v1/music/search/?${queryString}`,
            headers: {
                Accept: "application/json, text/plain, */*",
                "Accept-Language": "zh-CN,zh;q=0.9,en-US;q=0.8,en;q=0.7",
                "cache-control": "no-cache",
                pragma: "no-cache",
                priority: "u=1, i",
                origin: "https://creator.douyin.com",
                referer: "https://creator.douyin.com/",
                "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
                "sec-ch-ua-mobile": "?0",
                "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
                "sec-fetch-dest": "empty",
                "sec-fetch-mode": "cors",
                "sec-fetch-site": "cross-site",
                "user-agent": userAgent,
                "agw-auth": authorization?.signature || "",
                "openapi-omit-shark": "1",
            },
            responseType: "arraybuffer",
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );

    // 解压 zstd
    const decompressed = decompress(new Uint8Array(musicRes));
    const res: DouyinMusicResponse = JSON.parse(Buffer.from(decompressed).toString("utf-8"));
    console.log("抖音获取音乐响应:", JSON.stringify(res));

    const isSuccess = res.status_code === 0;
    const message = `抖音获取音乐${isSuccess ? "成功" : `失败，原因：${res.status_msg}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data: { music: Music[]; total: number } = isSuccess
        ? { music: res?.music || [], total: res?.music?.length || 0 }
        : { music: [], total: 0 };

    return response(isSuccess ? 0 : 414, message, data);
};
